import React, {Fragment, useState, useEffect, useCallback} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import SaleDeleteForm from "./SaleDeleteForm";
import SaleUpdateForm from "./SaleUpdateForm";
import SaleItem from "./SaleItem";
import SaleCreateForm from "./SaleCreateForm";
import {getSales} from "../../actions/sale";
import FilterByClient from "./FilterByClient";
import FilterByProduct from "./FilterByProduct";
import FilterIsSpecialSale from "../filters/FilterIsSpecialSale"; 
import DeletedSalesFilterModal from "./DeletedSalesFilterModal";
import SearchForm from "../layout/SearchForm";
import {Plus, Filter} from "react-feather";
import TableSpinner from "../layout/TableSpinner";
import CustomPaginationWithManyPages from "../layout/CustomPaginationWithManyPages";
import FilterByDateRange from "../layout/FilterByDateRange";
import FilterBySaleType from "../filters/FilterBySaleType";
import { usePermission } from '../../hooks/usePermission';
import useActiveFirm from '../../hooks/useActiveFirm';
import '../../styles/materialImports.css';

const Sales = () => {
    const { userCan } = usePermission();
    const dispatch = useDispatch();
    const {sales, loading, numberOfPages, currentPage, total_sales} = useSelector(state => state.sale);
    const { firmId, firmName } = useActiveFirm();

    // Wrapper function to ensure firm_id is always included when calling getSales
    // Returns a thunk function (async action) that can be dispatched
    const getSalesWithFirm = useCallback((page, params = {}) => {
        const filters = { ...params };
        if (firmId) {
            filters.firm_id = firmId;
        }
        // Clean up undefined/null values
        Object.keys(filters).forEach(key => {
            if (filters[key] === undefined || filters[key] === null || filters[key] === '' || filters[key] === 'undefined' || filters[key] === 'null') {
                delete filters[key];
            }
        });
        // Return the thunk function, don't dispatch it
        return getSales(page, filters);
    }, [firmId]);

    // Fetch sales when firm changes
    useEffect(() => {
        if (firmId) {
            dispatch(getSalesWithFirm(1, {}));
        }
    }, [dispatch, firmId, getSalesWithFirm]);

    if (!userCan('view sales')) {
        return <div>You don't have permission to view sales.</div>;
    }

    const [showCreate, setShowCreate] = useState(false);
    const handleCreateClose = () => setShowCreate(false);
    const handleCreateShow = () => setShowCreate(true);

    const [showUpdate, setShowUpdate] = useState(false);
    const handleUpdateClose = () => setShowUpdate(false);
    const handleUpdateShow = () => {
        showLoader()
        setShowUpdate(true)
    };
    
    const [showDeletedFilters, setShowDeletedFilters] = useState(false);
    const handleDeletedFiltersClose = () => setShowDeletedFilters(false);
    const handleDeletedFiltersShow = () => setShowDeletedFilters(true);

    const [isLoading, setIsLoading] = useState(loading ?? false);
    const showLoader = () => setIsLoading(true)
    const hideLoader = () => setIsLoading(false)
    
    // Check if we're currently filtering for deleted items
    const params = new URLSearchParams(window.location.search);
    const hasDeletedFilters = params.has('with_trashed') || params.has('only_trashed');

    return (
        <Fragment>
            <div className="container-fluid p-0">
                <div className="row mb-2">
                    <div className="col-md-9 ">
                        <h1 className="h3 mb-3">
                            <strong>Sotuv bo'limi</strong> ({total_sales})
                            {firmName && <span className="text-muted ms-2" style={{fontSize: '14px', fontWeight: 'normal'}}>— {firmName}</span>}
                        </h1>
                    </div>

                    <div className="col-md-3 justify-content-end">
                        <div className="crud_actions text-end d-flex justify-content-end gap-2">
                            <button 
                                className={`btn ${hasDeletedFilters ? 'btn-warning' : 'btn-outline-secondary'}`} 
                                onClick={handleDeletedFiltersShow}
                            >
                                <Filter size={16} className="me-1" />
                                {hasDeletedFilters ? 'O\'chirilgan filtr faol' : 'O\'chirilgan filtr'}
                            </button>
                            
                            {userCan('create sales') && (
                                <button className="btn btn-primary" onClick={handleCreateShow}>
                                    Sotuv <Plus/>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-3">
                        <FilterByProduct getPageItems={getSalesWithFirm}/>
                    </div>

                    <div className="col-md-3">
                        <SearchForm getPageItems={getSalesWithFirm}/>
                    </div>
                    <div className="col-md-3">
                        <FilterByDateRange getPageItems={getSalesWithFirm}/>
                    </div>
                    <div className="col-md-3">
                        <FilterByClient getPageItems={getSalesWithFirm}/>
                    </div>
                </div>

                <div className="row align-items-center mt-2">
                    <div className="col-md-4">
                        <FilterBySaleType getPageItems={getSalesWithFirm}/>
                    </div>
                    <div className="col-md-4">
                        <FilterIsSpecialSale getPageItems={getSalesWithFirm}/>
                    </div>
                </div>

                <div className="row mt-4">
                    <div className="col-xl-12 col-xxl-12 d-flex">
                        <div className="card w-100 table-responsive">
                            {loading && <TableSpinner/>}
                            <table className="table custom-table table-hover">
                                <thead className="table-light">
                                <tr>
                                    <th scope="col">#</th>
                                    <th scope="col">Mahsulotlar/Materiallar</th>
                                    <th scope="col">Sotuvchi</th>
                                    <th scope="col">Firm</th>
                                    <th scope="col">Client</th>
                                    <th scope="col">Summa</th>
                                    <th scope="col">Sana</th>
                                    <th scope="col">Amal vaqti</th>
                                    <th scope="col">Turi</th>
                                    <th scope="col">Maxsus sotuvmi?</th>
                                    <th scope="col">Amallar</th>
                                </tr>
                                </thead>
                                <tbody>
                                {sales.map((item, index) => (
                                    <SaleItem 
                                        key={item.id} 
                                        sale={item} 
                                        number={index + 1} 
                                        handleShow={handleUpdateShow}
                                    />
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xl-12 col-xxl-12 d-flex">
                        <CustomPaginationWithManyPages
                            numberOfPages={numberOfPages}
                            getPageItems={getSalesWithFirm}
                            current_page={currentPage}
                        />
                    </div>
                </div>
            </div>

            {userCan('create sales') && (
                <SaleCreateForm 
                    show={showCreate} 
                    handleClose={handleCreateClose}
                />
            )}

            {userCan('edit sales') && (
                <SaleUpdateForm 
                    show={showUpdate}
                    handleShow={handleUpdateShow}
                    handleClose={handleUpdateClose}
                    isLoading={isLoading} 
                    showLoader={showLoader} 
                    hideLoader={hideLoader}
                />
            )}

            {userCan('delete sales') && (
                <SaleDeleteForm/>
            )}
            
            <DeletedSalesFilterModal
                show={showDeletedFilters}
                handleClose={handleDeletedFiltersClose}
                getPageItems={getSalesWithFirm}
            />
        </Fragment>
    );
};

export default Sales;
